/**
 * TOSS Web Application - Notification Badge Component
 *
 * Badge component for displaying unread notification count.
 * Features:
 * - Real-time count updates via TanStack Query
 * - Animated appearance for new notifications
 * - Maximum count display (99+)
 * - Hide when count is zero
 *
 * Usage:
 * - Place in header/navigation for global notification indicator
 * - Automatically polls for count updates
 * - Integrates with SignalR for real-time updates
 */

import { memo, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { ROUTES } from '@/constants';
import { useUnreadCount } from '@/hooks/api';

// ============================================================================
// Types
// ============================================================================

export interface NotificationBadgeProps {
  /** Maximum count to display before showing "+" */
  maxCount?: number;
  /** Show badge when count is zero */
  showZero?: boolean;
  /** Polling interval in milliseconds (0 to disable) */
  refetchInterval?: number;
  /** Make the badge a clickable link to notifications page */
  asLink?: boolean;
  /** Additional class names */
  className?: string;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
}

export interface NotificationIconBadgeProps extends Omit<NotificationBadgeProps, 'asLink' | 'maxCount'> {
  /** Icon to display */
  icon?: React.ReactNode;
}

// ============================================================================
// Size Styles
// ============================================================================

const badgeSizeStyles = {
  sm: {
    badge: 'w-[8px] h-[8px]',
    offset: '-top-0.5 -right-0.5',
  },
  md: {
    badge: 'w-[10px] h-[10px]',
    offset: '-top-0.5 -right-0.5',
  },
  lg: {
    badge: 'w-[12px] h-[12px]',
    offset: '-top-1 -right-1',
  },
};

// ============================================================================
// NotificationBadge Component
// ============================================================================

/**
 * Notification count badge
 *
 * @example
 * ```tsx
 * // Simple badge
 * <NotificationBadge />
 *
 * // As a link
 * <NotificationBadge asLink />
 *
 * // With custom max count
 * <NotificationBadge maxCount={9} />
 * ```
 */
export const NotificationBadge = memo(function NotificationBadge({
  maxCount = 99,
  showZero = false,
  refetchInterval = 30000,
  asLink = false,
  className = '',
  size = 'md',
}: NotificationBadgeProps): JSX.Element | null {
  const { data, isLoading } = useUnreadCount({
    refetchInterval,
  });

  const count = data?.count ?? 0;

  // Animation state for new notifications
  const [isAnimating, setIsAnimating] = useState(false);
  const [prevCount, setPrevCount] = useState(count);

  // Animate when count increases
  useEffect(() => {
    if (count > prevCount && count > 0) {
      setIsAnimating(true);
      const timer = setTimeout(() => setIsAnimating(false), 300);
      return () => clearTimeout(timer);
    }
    setPrevCount(count);
  }, [count, prevCount]);

  // Don't show if count is zero and showZero is false
  if (count === 0 && !showZero && !isLoading) {
    return null;
  }

  const displayCount = count > maxCount ? `${maxCount}+` : String(count);
  const sizeStyle = badgeSizeStyles[size];

  const badge = (
    <span
      className={`
        inline-flex items-center justify-center
        font-medium rounded-full
        bg-[var(--color-error)] text-white
        transition-transform
        ${sizeStyle.badge}
        ${isAnimating ? 'scale-125' : 'scale-100'}
        ${className}
      `}
      aria-label={`${count} непрочитанных уведомлений`}
    >
      {isLoading ? '...' : displayCount}
    </span>
  );

  if (asLink) {
    return (
      <Link
        to={ROUTES.notifications.list}
        className="relative inline-flex"
        aria-label={`Уведомления: ${count} непрочитанных`}
      >
        {badge}
      </Link>
    );
  }

  return badge;
});

// ============================================================================
// NotificationIconBadge Component
// ============================================================================

/**
 * Notification badge with icon (for header/navigation)
 *
 * @example
 * ```tsx
 * <NotificationIconBadge
 *   icon={<BellIcon />}
 *   maxCount={99}
 * />
 * ```
 */
export const NotificationIconBadge = memo(function NotificationIconBadge({
  icon,
  showZero = false,
  refetchInterval = 30000,
  className = '',
  size = 'md',
}: NotificationIconBadgeProps): JSX.Element {
  const { data } = useUnreadCount({
    refetchInterval,
  });

  const count = data?.count ?? 0;

  // Animation state for new notifications
  const [isAnimating, setIsAnimating] = useState(false);
  const [prevCount, setPrevCount] = useState(count);

  // Animate when count increases
  useEffect(() => {
    if (count > prevCount && count > 0) {
      setIsAnimating(true);
      const timer = setTimeout(() => setIsAnimating(false), 300);
      return () => clearTimeout(timer);
    }
    setPrevCount(count);
  }, [count, prevCount]);

  const sizeStyle = badgeSizeStyles[size];
  const showBadge = count > 0 || showZero;

  const defaultIcon = <Bell className="w-6 h-6" />;

  return (
    <Link
      to={ROUTES.notifications.list}
      className={`
        relative inline-flex items-center justify-center
        p-2 rounded-lg text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-tertiary)] transition-colors
        ${className}
      `}
      aria-label={`Уведомления${count > 0 ? `: ${count} непрочитанных` : ''}`}
    >
      {icon || defaultIcon}

      {showBadge && (
        <span
          className={`
            absolute rounded-full
            bg-[var(--color-error)]
            transition-transform
            ${sizeStyle.badge}
            ${sizeStyle.offset}
            ${isAnimating ? 'scale-125' : 'scale-100'}
          `}
          aria-label={`${count} непрочитанных уведомлений`}
        />
      )}
    </Link>
  );
});

// ============================================================================
// Connection Indicator Component
// ============================================================================

export interface ConnectionIndicatorProps {
  /** Connection state */
  state: 'Disconnected' | 'Connecting' | 'Connected' | 'Disconnecting' | 'Reconnecting';
  /** Show only when not connected */
  hideWhenConnected?: boolean;
  /** Additional class names */
  className?: string;
}

/**
 * SignalR connection state indicator
 *
 * @example
 * ```tsx
 * const { connectionState } = useRealtimeNotifications();
 *
 * <ConnectionIndicator state={connectionState} hideWhenConnected />
 * ```
 */
export const ConnectionIndicator = memo(function ConnectionIndicator({
  state,
  hideWhenConnected = false,
  className = '',
}: ConnectionIndicatorProps): JSX.Element | null {
  if (hideWhenConnected && state === 'Connected') {
    return null;
  }

  const stateConfig = {
    Disconnected: {
      color: 'bg-[var(--color-error)]',
      text: 'Отключено',
      animate: false,
    },
    Connecting: {
      color: 'bg-[var(--color-warning)]',
      text: 'Подключение...',
      animate: true,
    },
    Connected: {
      color: 'bg-[var(--color-success)]',
      text: 'Подключено',
      animate: false,
    },
    Disconnecting: {
      color: 'bg-[var(--color-warning)]',
      text: 'Отключение...',
      animate: true,
    },
    Reconnecting: {
      color: 'bg-[var(--color-warning)]',
      text: 'Переподключение...',
      animate: true,
    },
  };

  const config = stateConfig[state];

  return (
    <div
      className={`
        inline-flex items-center gap-2 text-xs text-[var(--color-text-secondary)]
        ${className}
      `}
      title={config.text}
    >
      <span
        className={`
          w-2 h-2 rounded-full
          ${config.color}
          ${config.animate ? 'animate-pulse' : ''}
        `}
      />
      <span className="hidden sm:inline">{config.text}</span>
    </div>
  );
});

export default NotificationBadge;
